/**
 * Format price with appropriate decimal places
 */
export const formatPrice = (price: number, decimals: number = 2): string => {
  if (price === 0) return '0.00';

  // For very small prices, show more decimals
  if (price < 0.01) {
    return price.toFixed(6);
  }

  // For prices less than 1, show 4 decimals
  if (price < 1) {
    return price.toFixed(4);
  }

  // For regular prices
  return price.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format percentage with sign
 */
export const formatPercentage = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

/**
 * Format large numbers with abbreviations
 */
export const formatMarketCap = (value: number): string => {
  if (value >= 1e12) {
    return `$${(value / 1e12).toFixed(2)}T`;
  }
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(2)}M`;
  }
  return `$${value.toLocaleString()}`;
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays}d ago`;
  }
  if (diffHours > 0) {
    return `${diffHours}h ago`;
  }
  if (diffMins > 0) {
    return `${diffMins}m ago`;
  }
  return 'Just now';
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    EUR: '\u20AC',
    TRY: '\u20BA',
    USDT: '$',
    USD: '$',
  };
  return symbols[currency] || currency;
};

/**
 * Format signal value based on type
 */
export const formatSignalValue = (value: number, type: string, currency?: string): string => {
  if (type === 'percent_change') {
    return `${value}%`;
  }

  const symbol = currency ? getCurrencySymbol(currency) : '';
  return `${symbol}${formatPrice(value)}`;
};
